/**
 * Cloudflare Turnstile, shaped around three constraints this site has.
 *
 * 1. TOKENS ARE SINGLE-USE. Cloudflare's siteverify consumes a token, and a
 *    replay comes back `timeout-or-duplicate`. The calculator is a chat, so a
 *    widget rendered once at page load would authorise the first message and
 *    silently fail every one after it. A fresh token is minted per request.
 *
 * 2. THE PAGE IS PRERENDERED. react-snap snapshots the live DOM in a headless
 *    browser, effects and all. A widget that mounted itself would be baked into
 *    build output — carrying a token already dead by the time anyone loaded the
 *    page, and risking the hydration mismatch that discards a whole prerendered
 *    route. Nothing here runs during the snapshot, and the container it renders
 *    into is empty until a real visitor sends a message.
 *
 * 3. IT MUST BE OPTIONAL. With no site key configured this disables itself and
 *    returns no token, mirroring the backend, where a blank TurnstileSecretKey
 *    disables verification. That way the two can be switched on independently
 *    and neither half breaks while the other is unconfigured.
 *
 * On failure — script blocked, network down, challenge timed out — this returns
 * null rather than throwing, and the request goes without a token. The decision
 * to accept or reject belongs to the handler, which is the only side that knows
 * whether verification is switched on. Failing hard here would block requests
 * the backend would have allowed.
 */

import { useCallback, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * How long to wait for a challenge before giving up and sending unverified.
 *
 * Generous, because `interaction-only` means Cloudflare may decide this visitor
 * has to click something, and a human needs longer than a script. This wait
 * happens BEFORE the request is issued, so it does not eat into the 30s the
 * proxy allows the agent turn itself.
 */
const CHALLENGE_TIMEOUT_MS = 30000;

/**
 * react-snap identifies itself with this exact user agent (its default, set in
 * node_modules/react-snap/index.js). Checking it is what keeps the build-time
 * snapshot free of any Turnstile DOM.
 */
function isPrerender() {
  return typeof navigator === 'undefined' || navigator.userAgent === 'ReactSnap';
}

let scriptPromise = null;

function loadScript() {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => {
      // Let a later attempt retry rather than caching the failure forever.
      scriptPromise = null;
      reject(new Error('Turnstile script did not load'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * @param {string} siteKey    public Turnstile site key; falsy disables the hook
 * @param {object} containerRef  ref to an empty div the widget renders into
 * @returns {() => Promise<string|null>} mints one fresh token, or null
 */
export function useTurnstile(siteKey, containerRef) {
  const widgetIdRef = useRef(null);
  const pendingRef = useRef(null);

  const settle = useCallback((token) => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    clearTimeout(pending.timer);
    pending.resolve(token);
  }, []);

  return useCallback(async () => {
    if (!siteKey || isPrerender() || !containerRef.current) return null;

    let turnstile;
    try {
      turnstile = await loadScript();
    } catch {
      return null;
    }
    if (!turnstile) return null;

    return new Promise((resolve) => {
      // A challenge that never calls back must not hang the send button.
      const timer = setTimeout(() => {
        if (pendingRef.current) {
          pendingRef.current = null;
          resolve(null);
        }
      }, CHALLENGE_TIMEOUT_MS);

      pendingRef.current = { resolve, timer };

      try {
        if (widgetIdRef.current == null) {
          // The challenge starts on render and reports back through `callback`.
          //
          // An earlier version used `execution: 'execute'` and called
          // turnstile.execute() on the next line. render() is not synchronously
          // ready, so execute() landed before the widget existed and did
          // nothing at all: the challenge never started, the token never came,
          // and the request went out unverified. With the backend enforcing,
          // that 403s every visitor. Rendering on demand — which is what keeps
          // this out of the prerendered snapshot — already gives us the lazy
          // behaviour that execute-mode was there for.
          widgetIdRef.current = turnstile.render(containerRef.current, {
            sitekey: siteKey,
            // Invisible unless Cloudflare decides this visitor must interact.
            appearance: 'interaction-only',
            callback: (token) => settle(token),
            'error-callback': () => settle(null),
            'timeout-callback': () => settle(null),
            'expired-callback': () => settle(null),
          });
          if (widgetIdRef.current == null) settle(null);
        } else {
          // Discard the spent token and re-run: a replayed token comes back
          // from siteverify as timeout-or-duplicate. reset() re-fires callback.
          turnstile.reset(widgetIdRef.current);
        }
      } catch {
        settle(null);
      }
    });
  }, [siteKey, containerRef, settle]);
}
